import {
  ControlledCheckbox,
  ControlledDateOfBirthInput,
  ControlledInput,
} from "components/Card/ControlledInput";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { useFileDOI } from "components/DOIHeaderSelect/useFileDOI";
import { MaskedInput, TextInput } from "components/inputs";
import ControlledSelect from "components/inputs/Select/ControlledSelect";
import { GENDERS } from "constants/FormConstants";
import { useEffect, useMemo, useState } from "react";
import type { Path } from "react-hook-form";
import { useForm } from "react-hook-form";
import type { AmendedReviewScreeningType } from "types/DataTypes";
import { useSelector } from "utils/redux";

type Props = {
  onSave: (field: string | null, value: unknown) => void;
  service: AmendedReviewScreeningType;
  section: string;
  className?: string;
};

type FormValues = {
  "claimant.first_name": string;
  "claimant.last_name": string;
  "date_of_injury.jurisdiction": string;
  "date_of_injury.wcmsa_zip_code": string;
  "date_of_injury.one_eleven_jurisdiction": string;
  "claimant.ssn": string;
  "claimant.medicare_id": string;
  "claimant.address": string;
  "claimant.state": string;
  "claimant.zip_code": string;
  "claimant.birth_date": string | Date | null;
  "claimant.gender": string;
  "claimant.is_medicare_eligible": boolean;
  "claimant.reasonable_expectation": boolean;
  claimant_chronological_age: string;
  claimant_rated_age: string;
  claimant_life_expectancy: string;
};

/**
 * ARSClaimantInformation handles claimant information specifically for the Amended Review Screening type.
 * unlike MSA and CostProjection, ARS uses the file object to access claimant data and DOI is file-based.
 */
const ARSClaimantInformation = ({
  onSave,
  service,
  section,
  className = "",
}: Props) => {
  const constants = useSelector((state) => state.shared.constants);

  // get claimant data from the first entry in the file.claimants array
  const claimant = service?.file?.claimants?.[0];

  // for ARS, we use file-based DOI handling
  const { selectedDOI } = useFileDOI(service.file);
  const [selectedDoiId, setSelectedDoiId] = useState<number | null>(null);

  const {
    control,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      "claimant.first_name": claimant?.first_name || "",
      "claimant.last_name": claimant?.last_name || "",
      // use actual DOI properties instead of ServiceDOI for WCMSA and 111 Jurisdiction
      "date_of_injury.jurisdiction": selectedDOI?.jurisdiction || "",
      "date_of_injury.wcmsa_zip_code": selectedDOI?.wcmsa_zip_code || "",
      "date_of_injury.one_eleven_jurisdiction":
        selectedDOI?.one_eleven_jurisdiction || "",
      "claimant.ssn": claimant?.ssn || "",
      "claimant.medicare_id": claimant?.medicare_id || "",
      "claimant.address": claimant?.address || "",
      "claimant.state": claimant?.state || "",
      "claimant.zip_code": claimant?.zip_code || "",
      "claimant.birth_date": claimant?.birth_date || null,
      "claimant.gender": claimant?.gender || "",
      "claimant.is_medicare_eligible": claimant?.is_medicare_eligible || false,
      "claimant.reasonable_expectation":
        claimant?.reasonable_expectation || false,
      claimant_chronological_age:
        service?.claimant_chronological_age?.toString() || "",
      claimant_rated_age: service?.claimant_rated_age?.toString() || "",
      claimant_life_expectancy:
        service?.claimant_life_expectancy?.toString() || "",
    },
  });

  // update form values when selected DOI changes
  useEffect(() => {
    if (selectedDOI) {
      setSelectedDoiId(selectedDOI.id);

      const doiFields = {
        "date_of_injury.jurisdiction": selectedDOI.jurisdiction,
        "date_of_injury.wcmsa_zip_code": selectedDOI.wcmsa_zip_code,
        "date_of_injury.one_eleven_jurisdiction":
          selectedDOI.one_eleven_jurisdiction,
      } as const;

      Object.entries(doiFields).forEach(([field, value]) => {
        setValue(field as Path<FormValues>, value ?? "");
      });
    }
  }, [selectedDOI, setValue]);

  const handleSave = async (field: string, value: unknown) => {
    onSave(field, value);
    return Promise.resolve();
  };

  const claimantInfoContent = useMemo(
    () => (
      <>
        <FutureMedicalCard heading="Claimant Information">
          <ControlledInput
            control={control}
            name="claimant.first_name"
            label="First Name"
            errors={errors}
            onSave={(value) => handleSave("claimant.first_name", value)}
            componentType={TextInput}
          />
          <ControlledInput
            control={control}
            name="claimant.last_name"
            label="Last Name"
            errors={errors}
            onSave={(value) => handleSave("claimant.last_name", value)}
            componentType={TextInput}
          />
          <ControlledSelect
            key={`jurisdiction-${selectedDoiId}`}
            control={control}
            name="date_of_injury.jurisdiction"
            label="WCMSA State"
            errors={errors}
            options={constants?.STATES || []}
            onSave={(option) =>
              handleSave("date_of_injury.jurisdiction", option?.value)
            }
          />
          <ControlledInput
            key={`wcmsa-zip-${selectedDoiId}`}
            control={control}
            name="date_of_injury.wcmsa_zip_code"
            label="WCMSA Zip Code"
            errors={errors}
            onSave={(value) =>
              handleSave("date_of_injury.wcmsa_zip_code", value)
            }
            componentType={MaskedInput}
            mask="zipcode-nine"
          />
          <ControlledSelect
            key={`111-jurisdiction-${selectedDoiId}`}
            control={control}
            name="date_of_injury.one_eleven_jurisdiction"
            label="111 Jurisdiction"
            errors={errors}
            options={constants?.JURISDICTIONS || []}
            onSave={(option) =>
              handleSave(
                "date_of_injury.one_eleven_jurisdiction",
                option?.value,
              )
            }
          />
          <ControlledInput
            control={control}
            name="claimant.ssn"
            label="SSN"
            errors={errors}
            onSave={(value) => handleSave("claimant.ssn", value)}
            componentType={MaskedInput}
            mask="ssn"
          />
          <ControlledInput
            control={control}
            name="claimant.medicare_id"
            label="Medicare ID"
            errors={errors}
            onSave={(value) => handleSave("claimant.medicare_id", value)}
            componentType={MaskedInput}
            mask="medicare"
          />
          <ControlledInput
            control={control}
            name="claimant.address"
            label="Claimant's Address"
            errors={errors}
            onSave={(value) => handleSave("claimant.address", value)}
            componentType={TextInput}
          />
          <ControlledSelect
            control={control}
            name="claimant.state"
            label="State"
            errors={errors}
            options={constants?.STATES || []}
            onSave={(option) => handleSave("claimant.state", option?.value)}
          />
          <ControlledInput
            control={control}
            name="claimant.zip_code"
            label="Zip"
            errors={errors}
            onSave={(value) => handleSave("claimant.zip_code", value)}
            componentType={MaskedInput}
            mask="zipcode-nine"
          />
          <ControlledDateOfBirthInput
            control={control}
            name="claimant.birth_date"
            errors={errors}
            onSave={(value) => handleSave("claimant.birth_date", value)}
          />
          <ControlledSelect
            control={control}
            name="claimant.gender"
            label="Gender"
            errors={errors}
            options={GENDERS}
            onSave={(option) => handleSave("claimant.gender", option?.value)}
          />
          <ControlledCheckbox
            control={control}
            name="claimant.is_medicare_eligible"
            label="Is Medicare Eligible"
            errors={errors}
            onSave={(value) =>
              handleSave("claimant.is_medicare_eligible", value)
            }
          />
          <ControlledCheckbox
            control={control}
            name="claimant.reasonable_expectation"
            label="Reasonable Expectation"
            errors={errors}
            onSave={(value) =>
              handleSave("claimant.reasonable_expectation", value)
            }
          />
        </FutureMedicalCard>

        {(section === "review" || section === "client-analysis") && (
          <FutureMedicalCard heading="Rated Ages and Life Expectancy">
            <ControlledInput
              control={control}
              name="claimant_chronological_age"
              label="Chronological Age"
              errors={errors}
              onSave={(value) =>
                handleSave("claimant_chronological_age", value)
              }
              componentType={MaskedInput}
              mask="age"
            />
            <ControlledInput
              control={control}
              name="claimant_rated_age"
              label="Rated Age"
              errors={errors}
              onSave={(value) => handleSave("claimant_rated_age", value)}
              componentType={MaskedInput}
              mask="age"
            />
            <ControlledInput
              control={control}
              name="claimant_life_expectancy"
              label="Life Expectancy"
              errors={errors}
              onSave={(value) => handleSave("claimant_life_expectancy", value)}
              componentType={MaskedInput}
              mask="age"
            />
          </FutureMedicalCard>
        )}
      </>
    ),
    [control, errors, constants, section, handleSave, selectedDoiId],
  );

  return (
    <div className={`ARSClaimantInformation ${className}`}>
      <div className="swim-headers">Claimant Information</div>
      <div className="swim-column">{claimantInfoContent}</div>
    </div>
  );
};

export default ARSClaimantInformation;
